/**
 * Leaf-level diff of two runtime documents.
 *
 * <h3>Why this exists</h3>
 * A push meant to change ONE tool URL once rewrote 94 of 154 leaves of a live
 * agent: it deleted the whole gemini_live block (VAD sensitivities, silence
 * duration, ringback, welcome delay), flipped the toolchain from realtime to a
 * streaming pipeline, and switched the codec from mulaw to pcm. It was
 * "verified" against fourteen hand-picked fields and reported as two changes.
 *
 * The lesson was not "check more fields" — it was that a subset can never tell
 * you what a whole-document push does. So the editor compares EVERY leaf, and
 * shows the count before anything is sent.
 */

export interface LeafChange {
  path: string;
  from?: unknown;
  to?: unknown;
}

export interface DocDiff {
  changed: LeafChange[];
  added: LeafChange[];
  removed: LeafChange[];
  /** Total leaves affected — the number that matters before pushing. */
  total: number;
  /** Leaves examined on the incoming document, so "0 of 154" reads honestly. */
  leavesCompared: number;
}

/**
 * Every scalar in the document, keyed by its path.
 *
 * Arrays are indexed rather than compared as wholes: `tasks[0].toolchain` moving
 * is precisely the kind of change that must not hide inside "the tasks array
 * differs". An empty object or array is itself a leaf — replacing a populated
 * gemini_live block with `{}` has to register as a change, not vanish because
 * it has no scalars left to walk.
 */
export function flattenLeaves(value: unknown, prefix = ''): Map<string, unknown> {
  const out = new Map<string, unknown>();

  const walk = (v: unknown, path: string) => {
    if (Array.isArray(v)) {
      if (v.length === 0) { out.set(path, '[]'); return; }
      v.forEach((item, i) => walk(item, `${path}[${i}]`));
      return;
    }
    if (v !== null && typeof v === 'object') {
      const keys = Object.keys(v as Record<string, unknown>);
      if (keys.length === 0) { out.set(path, '{}'); return; }
      keys.forEach(k => walk((v as Record<string, unknown>)[k], path ? `${path}.${k}` : k));
      return;
    }
    out.set(path, v);
  };

  walk(value, prefix);
  return out;
}

/** Compare `next` against `current`, leaf by leaf. */
export function diffDocuments(current: unknown, next: unknown): DocDiff {
  const a = flattenLeaves(current);
  const b = flattenLeaves(next);

  const changed: LeafChange[] = [];
  const added: LeafChange[] = [];
  const removed: LeafChange[] = [];

  b.forEach((to, path) => {
    if (!a.has(path)) { added.push({ path, to }); return; }
    const from = a.get(path);
    if (!Object.is(from, to)) changed.push({ path, from, to });
  });
  a.forEach((from, path) => {
    if (!b.has(path)) removed.push({ path, from });
  });

  return {
    changed, added, removed,
    total: changed.length + added.length + removed.length,
    leavesCompared: b.size,
  };
}

/** Short, readable rendering of a value for the diff list. */
export function preview(v: unknown, max = 60): string {
  if (v === undefined) return '(absent)';
  if (v === null) return 'null';
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}
